import type { ErrorResponse, FilterInfo, List } from '@/types/common';
import type { Contact, ContactFilter, CreateContactStatusResponse, CreateContactResponse, ContactListResponse, UnsubscribeRequest } from '@/types/contact';
import type { GroupInList } from '@/types/group';
import type {
	UserEventResponse,
	TicketDetailResponse,
	UserGroupRoleResponse,
	TicketOwnershipResponse,
	CardListResponse,
	UserProfileResponse,
	UserEventAttendingFilterInfo,
	UserEventHostingFilterInfo,
} from '@/types/profile';
import type { TicketReceiptResponse } from '@/types/ticket';

export const getUserInfoByIdAPI = (id: number) => {
	return useRequest<UserProfileResponse>(`${USERS_ENDPOINT}/${id}`, REQUEST_METHOD.GET);
};

export const getUserAttendingEventByIdAPI = (id: number, filter: UserEventAttendingFilterInfo) => {
	return useRequest<List<UserEventResponse>>(`${USERS_ENDPOINT}/${id}/events`, REQUEST_METHOD.GET, {}, filter);
};

export const getUserHostingEventByIdAPI = (id: number, filter: UserEventHostingFilterInfo) => {
	return useRequest<List<UserEventResponse>>(`${USERS_ENDPOINT}/${id}/events-hosting`, REQUEST_METHOD.GET, {}, filter);
};

export const getUserAttendanceByEventId = (userId: number, eventId: number) => {
	return useRequest<TicketDetailResponse, ErrorResponse>(`${USERS_ENDPOINT}/${userId}/events-attendances`, REQUEST_METHOD.GET, {}, {
		eventId,
	});
};

export const getUserGroupRole = (profileId: number, userGroupId: number) => {
	return useRequest<UserGroupRoleResponse, ErrorResponse>(`${USERS_ENDPOINT}/${profileId}/account/groups/${userGroupId}`, REQUEST_METHOD.GET);
};

export const getTicketOwnership = (eventId: number) => {
	return useRequest<TicketOwnershipResponse, ErrorResponse>(`${USERS_ENDPOINT}/me/events-attending/${eventId}`, REQUEST_METHOD.GET);
};

export const postFollowGroup = (userId: number, groupId: number, role: string) => {
	return useRequest<{}, ErrorResponse>(`${USERS_ENDPOINT}/${userId}/account/groups`, REQUEST_METHOD.POST, {
		groupId,
		role,
	});
};

export const deleteUnFollowGroup = (userId: number, groupId: number) => {
	return useRequest<{}, ErrorResponse>(`${USERS_ENDPOINT}/${userId}/account/groups/${groupId}`, REQUEST_METHOD.DELETE);
};

export const getCardListByUserId = (userId: number, filterInfo?: FilterInfo) => {
	return useRequest<CardListResponse, ErrorResponse>(`${USERS_ENDPOINT}/${userId}/account/payment-cards`, REQUEST_METHOD.GET, {}, {
		sort: filterInfo?.sortBy,
		page: filterInfo?.currentPage,
		limit: filterInfo?.itemPerPage,
	});
};

export const deleteCardById = (userId: number, cardId: string) => {
	return useRequest<{}, ErrorResponse>(`${USERS_ENDPOINT}/${userId}/account/payment-cards`, REQUEST_METHOD.DELETE, {
		stripePaymentMethodId: cardId,
	});
};

export const postTicketReceiptById = (userId: number, salesId: number) => {
	return useRequest<TicketReceiptResponse, ErrorResponse>(`${USERS_ENDPOINT}/${userId}/account/ticket-purchases/${salesId}/receipt`, REQUEST_METHOD.POST);
};

export const getGroupListByUserIdAPI = (userId: number, filter: FilterInfo) => {
	return useRequest<List<GroupInList>>(`${USERS_ENDPOINT}/${userId}/groups`, REQUEST_METHOD.GET, {}, filter);
};

export const getContactListByUserIdAPI = (filter: ContactFilter) => {
	return useRequest<ContactListResponse, ErrorResponse>(`${MY_USER_ACCOUNT_ENDPOINT}/contacts`, REQUEST_METHOD.GET, {}, filter);
};

export const postCreateContactAPI = (contacts: Contact[]) => {
	return useRequest<CreateContactResponse, ErrorResponse>(`${MY_USER_ACCOUNT_ENDPOINT}/contacts`, REQUEST_METHOD.POST, {
		contacts,
	});
};

export const getCreateContactStatusAPI = (requestId: string) => {
	return useRequest<CreateContactStatusResponse, ErrorResponse>(`${MY_USER_ACCOUNT_ENDPOINT}/contact-requests/${requestId}`, REQUEST_METHOD.GET);
};

export const deleteContactAPI = (id: number) => {
	return useRequest<{}, ErrorResponse>(`${MY_USER_ACCOUNT_ENDPOINT}/contacts/${id}`, REQUEST_METHOD.DELETE);
};

export const postUnsubscribeAPI = (unsubscribePayload: UnsubscribeRequest) => {
	return useRequest<{}, ErrorResponse>(`/event-invitation/opt-out`, REQUEST_METHOD.POST, {
		...unsubscribePayload,
	});
};